import { useQuery } from '@tanstack/react-query'
import { Select, Input } from 'antd'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ViewIcon from '@assets/icons/viewSvg'
import Header from '@components/header'
import Layout from '@components/Layout'
import ViewSalesModal from '@components/Modal/ViewSalesWithDocEntryModal'
import { useDebounce } from '@hooks/useDebounce'
import { downloadExcel } from '@utils/downloadPdf'
import { fetchGoodIssueReport, fetchDepartments } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

const GoodIssueReport = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const formattedDate = moment().format('YYYY-MM-DD')

	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(formattedDate)
	const [department, setDepartment] = useState('')
	const [docNum, setDocNum] = useState('')
	const [salesOrderDocNum, setSalesOrderDocNum] = useState('')
	const [departmentName, setDepartmentName] = useState('')
	const [itemCode, setItemCode] = useState('')
	const [itemName, setItemName] = useState('')
	const [master, setMaster] = useState('')
	const [masterName, setMasterName] = useState('')
	const [absoluteStatus, setAbsoluteStatus] = useState('')


	const debouncedDocNum = useDebounce(docNum, 500)
	const debouncedSalesOrderDocNum = useDebounce(salesOrderDocNum, 500)
	const debouncedDepartmentName = useDebounce(departmentName, 500)
	const debouncedItemCode = useDebounce(itemCode, 500)
	const debouncedItemName = useDebounce(itemName, 500)
	const debouncedMaster = useDebounce(master, 500)
	const debouncedMasterName = useDebounce(masterName, 500)
	const debouncedAbsoluteStatus = useDebounce(absoluteStatus, 500)

	const [pageSize] = useState(20)
	const [page, setPage] = useState(0)
	const [loadingExcel, setLoadingExcel] = useState(false)
	const [viewModalOpen, setViewModalOpen] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'good-issue-report',
			pageSize,
			page,
			startDate,
			endDate,
			department,
			debouncedDocNum,
			debouncedSalesOrderDocNum,
			debouncedDepartmentName,
			debouncedItemCode,
			debouncedItemName,
			debouncedMaster,
			debouncedMasterName,
			debouncedAbsoluteStatus,
		],
		queryFn: fetchGoodIssueReport,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data: departmentData = [] } = useQuery({
		queryKey: ['departments'],
		queryFn: fetchDepartments,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleViewClick = (record) => {
		setSelectedRowData(record)
		setViewModalOpen(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const baseColumns = [
		{
			title: t('docNum'),
			dataIndex: 'docNum',
			key: 'docNum',
		},
		{
			title: t('docDate'),
			dataIndex: 'docDate',
			key: 'docDate',
			render: (value) => moment(value).format('DD.MM.YYYY'),
		},
		{
			title: t('salesOrderDocNum'),
			dataIndex: 'salesOrderDocNum',
			key: 'salesOrderDocNum',
		},
		{
			title: t('department'),
			dataIndex: 'departmentName',
			key: 'departmentName',
		},

		{
			title: t('dscription'),
			dataIndex: 'dscription',
			key: 'dscription',
		},
		{
			title: t('quantity'),
			dataIndex: 'quantity',
			key: 'quantity',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('master'),
			dataIndex: 'master',
			key: 'master',
		},
		{
			title: t('absoluteStatus'),
			dataIndex: 'U_AbsoluteStatus',
			key: 'U_AbsoluteStatus',
		},
	]

	const columns = [...baseColumns]

	const handleNextPage = () => {
		if (data.length === pageSize) {
			setPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (page > 0) {
			setPage((prevPage) => prevPage - 1)
		}
	}

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/reports/goods-issue-list/export',
			params: {
				department,
				startDate,
				endDate,
				docNum: debouncedDocNum,
				salesOrderDocNum: debouncedSalesOrderDocNum,
				departmentName: debouncedDepartmentName,
				itemCode: debouncedItemCode,
				itemName: debouncedItemName,
				master: debouncedMaster,
				masterName: debouncedMasterName,
				absoluteStatus: debouncedAbsoluteStatus,
				page: 0,
				pageSize: 1000,
			},
			fileName: t('goodIssue'),
			onLoadingChange: setLoadingExcel,
		})
	}

	return (
		<Layout>
			<Header
				title={'goodIssue'}
				columns={columns}
				fdata={data}
				loading={isLoading}
				hasMoreData={data.length === pageSize}
				currentPage={page + 1}
				handlePreviousPage={handlePreviousPage}
				handleNextPage={handleNextPage}
				formattedDate={formattedDate}
				loadingExcel={loadingExcel}
				handleDownloadExcel={handleDownloadExcel}
				isShowExcelBtn
				renderFilters={() => {
					return (
						<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 items-end gap-4">
						<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('startDate')}</p>
								<Input
									value={startDate}
									onChange={(e) => handleStartDate(e)}
									placeholder={t('startDate')}
																		type="date"

									allowClear
								/>
							</div>
						<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('endDate')}</p>
								<Input
									value={endDate}
									type="date"
									onChange={(e) => handleEndDate(e)}
									placeholder={t('endDate')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-2">

								<p className="font-nunito">{t('department')}</p>
								<Select
									value={department}
									className="flex h-8 w-full items-center justify-center "
									style={{ color: 'black' }}
									onSelect={(val) => {
										setDepartment(val)
									}}
									options={departmentData.map((department) => {
										return {
											label: department.name,
											value: Number(department.code),
										}
									})}
									filterOption={(input, option) =>
										option.label
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									showSearch
									allowClear
									onClear={() => {
										setDepartment('')
									}}
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('docNum')}</p>
								<Input
									value={docNum}
									onChange={(e) => setDocNum(e.target.value)}
									placeholder={t('docNum')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('salesOrderDocNum')}</p>
								<Input
									value={salesOrderDocNum}
									onChange={(e) => setSalesOrderDocNum(e.target.value)}
									placeholder={t('salesOrderDocNum')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('itemName')}</p>
								<Input
									value={itemName}
									onChange={(e) => setItemName(e.target.value)}
									placeholder={t('itemName')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('masterName')}</p>
								<Input
									value={masterName}
									onChange={(e) => setMasterName(e.target.value)}
									placeholder={t('masterName')}
									allowClear
								/>
							</div>
						</div>
					)
				}}
			/>
			<ViewSalesModal
				visible={viewModalOpen}
				data={selectedRowData}
				onClose={() => setViewModalOpen(false)}
			/>
		</Layout>
	)
}

export default GoodIssueReport
